import { useAtomValue, useStore } from "jotai";
import {
  isSegmentationActiveAtom,
  segmentationVolumeIdAtom,
  segmentationAssistAtom,
  segEngineAtom,
} from "../atoms/segmentationAtoms";
import {
  activeMeasurementToolAtom,
  annotationSaveCountAtom,
  previousDrawingToolAtom,
} from "../atoms/measurementAtoms";
import { isV2ToolEnabled } from "../utils/viewportToolSetup";
import { getMultiSessions, hasNnInteractiveSession } from "../tools/RegionGrowTool";
import { showNnToast } from "../tools/nnInteractiveSession";

/** Shared reader-study button/keyboard actions. This hook registers no listeners. */
export function useSegmentationToolActions({
  enabled = true,
  engineAvailable = true,
  saving = false,
}: { enabled?: boolean; engineAvailable?: boolean; saving?: boolean } = {}) {
  const store = useStore();
  // Subscribe for button state; read the store again when an action is invoked.
  const active = useAtomValue(isSegmentationActiveAtom);
  const volume = useAtomValue(segmentationVolumeIdAtom);
  const saveCount = useAtomValue(annotationSaveCountAtom);
  const config = useAtomValue(segmentationAssistAtom);
  const canSelect = enabled && !!volume && active && !saving && saveCount === 0;
  const ready = () =>
    enabled &&
    !!store.get(segmentationVolumeIdAtom) &&
    store.get(isSegmentationActiveAtom) &&
    !saving &&
    store.get(annotationSaveCountAtom) === 0;

  return {
    canSelect,
    canGrow: canSelect && isV2ToolEnabled(config, "region_grow"),
    canSwitchEngine: canSelect && engineAvailable,
    selectTool(tool: "lasso" | "bump") {
      if (!ready()) return;
      // Explicit modes: repeating Lasso/Bump must keep the tool selected.
      store.set(activeMeasurementToolAtom, tool);
    },
    toggleRegionGrow() {
      if (!ready() || !isV2ToolEnabled(store.get(segmentationAssistAtom), "region_grow")) return;
      const current = store.get(activeMeasurementToolAtom);
      if (current === "regionGrow") {
        // Match the explicit None command, not its previous-tool restore path.
        store.set(activeMeasurementToolAtom, "none");
        store.set(previousDrawingToolAtom, null);
        return;
      }
      if (hasNnInteractiveSession()) {
        const sessions = getMultiSessions();
        if (!Array.from(sessions.values()).some((session) => session.contourUIDs.length > 0)) {
          sessions.clear();
        }
      }
      if (current === "lasso" || current === "bump") {
        store.set(previousDrawingToolAtom, current);
      }
      store.set(activeMeasurementToolAtom, "regionGrow");
    },
    toggleEngine() {
      if (!ready() || !engineAvailable) return;
      const next = store.get(segEngineAtom) === "nnInteractive" ? "threshold" : "nnInteractive";
      store.set(segEngineAtom, next);
      showNnToast(
        next === "nnInteractive" ? "Segmentation: AI Segmentation" : "Segmentation: Threshold",
      );
    },
  };
}
